import { useCallback, useEffect, useState } from 'react';

import useFlyingFrom from '@shared/v2/hooks/useFlyingFrom';

import useSearchQuery from '@/hooks/useSearchQuery';
import filterCampaignPropertiesByDestination from '../../utils/filterCampaignPropertiesByDestination';
import filterCampaignPropertiesByOrigin from '../../utils/filterCampaignPropertiesByOrigin';
import filterDestinationsFromCampaignProperties from '../../utils/filterDestinationsFromCampaignProperties';
import getOriginsFromProperties from '../../utils/getOriginsFromProperties';

const useCampaignData = (destinations, tiles) => {
  const {
    searchQuery: { destination: destinationName },
    setSearchQuery,
  } = useSearchQuery();

  const [originCodes, setOriginCodes] = useState([]);
  const [destinationNames, setDestinationNames] = useState([]);

  const [filteredProperties, setFilteredProperties] = useState(tiles);
  const [filteredDestinations, setFilteredDestinations] = useState([]);

  const setDestinationName = useCallback(
    (value) => {
      setSearchQuery({ destination: value || undefined });
    },
    [setSearchQuery],
  );

  const { originCode, setOriginCode } = useFlyingFrom(originCodes);

  useEffect(() => {
    const filteredByOrigin = filterCampaignPropertiesByOrigin(
      tiles,
      originCode,
    );

    setDestinationNames([
      ...new Set(
        filteredByOrigin.map(
          (property) => property.propertyContent.destination.name,
        ),
      ),
    ]);

    const updatedProperties = filterCampaignPropertiesByDestination(
      filteredByOrigin,
      destinationName,
    );

    // If the origin - destination pair is invalid and results in
    // no properties displayed, we reset the destination
    if (updatedProperties.length < 1) {
      setDestinationName(null);
    } else {
      setFilteredProperties(updatedProperties);
    }
  }, [
    tiles,
    destinationName,
    originCode,
    setDestinationName,
    setFilteredProperties,
  ]);

  useEffect(() => {
    setOriginCodes(getOriginsFromProperties(tiles));

    setFilteredDestinations(
      filterDestinationsFromCampaignProperties(
        destinations,
        filteredProperties,
      ),
    );
  }, [destinations, tiles, filteredProperties]);

  return {
    originCode,
    setOriginCode,
    originCodes,
    destinationName,
    setDestinationName,
    destinationNames,
    filteredProperties,
    filteredDestinations,
  };
};

export default useCampaignData;
